import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/utils/cn';
import { StepProps } from '../types';

/**
 * PlatformStep - Step 1 of AI Briefing Wizard
 *
 * Allows user to select target platform and define campaign objective.
 * Extracted from AIBriefingModal for wizard flow.
 */

// Platform options with FontAwesome brand icons
interface PlatformOption {
  value: string;
  label: string;
  faIcon: IconDefinition;
  color: string;
}

const PLATFORMS: PlatformOption[] = [
  { value: 'linkedin', label: 'LinkedIn', faIcon: faLinkedin, color: '#0A66C2' },
  { value: 'instagram', label: 'Instagram', faIcon: faInstagram, color: '#E4405F' },
  { value: 'facebook', label: 'Facebook', faIcon: faFacebook, color: '#1877F2' },
  { value: 'threads', label: 'Threads', faIcon: faThreads, color: '#000000' },
  { value: 'pinterest', label: 'Pinterest', faIcon: faPinterest, color: '#E60023' },
  { value: 'tiktok', label: 'TikTok', faIcon: faTiktok, color: '#000000' },
  { value: 'youtube', label: 'YouTube', faIcon: faYoutube, color: '#FF0000' },
  { value: 'bluesky', label: 'BlueSky', faIcon: faBluesky, color: '#0085FF' },
  { value: 'google_my_business', label: 'Google My Business', faIcon: faGoogle, color: '#4285F4' },
];

export const PlatformStep = ({ formData, onFieldChange, errors }: StepProps) => {
  const { t } = useTranslation('ai');

  return (
    <div className="space-y-5">
      {/* Platform Selector Grid */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('ai_briefing_platform_label')}
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {PLATFORMS.map((platform) => (
            <button
              key={platform.value}
              type="button"
              onClick={() => onFieldChange('platform', platform.value)}
              className={cn(
                'flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all',
                formData.platform === platform.value
                  ? 'border-primary-400 bg-soft-mint shadow-sm'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              )}
              aria-pressed={formData.platform === platform.value}
              aria-label={`Select ${platform.label}`}
            >
              <FontAwesomeIcon
                icon={platform.faIcon}
                className="text-lg"
                style={{ color: formData.platform === platform.value ? platform.color : '#6b7280' }}
              />
              <span className={cn(
                'text-xs font-medium',
                formData.platform === platform.value ? 'text-gray-900' : 'text-gray-500'
              )}>
                {platform.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Campaign Objective */}
      <div>
        <Textarea
          label={t('ai_briefing_objective_label')}
          placeholder={t('ai_briefing_objective_placeholder')}
          value={formData.objective}
          onChange={(e) => onFieldChange('objective', e.target.value)}
          rows={3}
          autoResize
          error={errors?.objective}
          required
        />
      </div>
    </div>
  );
};
